/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import Img from "../components/Img";

const DECISIONS = ["workspace", "venue", "mixed", "location_only", "archive", "needs_more_research"];

function StatusPill({ status }) {
  const colors = {
    synced: "#2f7d32", pending: "#8a6d15", retrying: "#a04b00",
    failed: "#a12b1c", null: "#666", undefined: "#666",
    ok: "#2f7d32", warn: "#8a6d15", mismatch: "#a04b00", payment_risk: "#a12b1c",
  };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: (colors[status] || "#666") + "22",
      color: colors[status] || "#666",
      fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
      fontFamily: "'Jost', sans-serif",
    }}>{status || "—"}</span>
  );
}

export default function AdminInventoryV2() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [deferred, setDeferred] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [o, r, d, ob] = await Promise.all([
        api.get("/admin/inventory-v2/overview"),
        api.get("/admin/inventory-v2/records"),
        api.get("/admin/inventory-v2/deferred"),
        api.get("/admin/inventory-v2/outbox"),
      ]);
      setOverview(o.data);
      setRecords(r.data.records);
      setDeferred(d.data.deferred);
      setOutbox(ob.data.events);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{padding:40, fontFamily:"'Jost', sans-serif"}}>Loading Inventory V2…</div>;
  if (error) return <div style={{padding:40, color:"#a12b1c", fontFamily:"'Jost', sans-serif"}}>Error: {error} <br/><small>(Feature flag FEATURE_INVENTORY_V2 must be set to <code>true</code> in the backend env for the preview to be reachable.)</small></div>;

  return (
    <div style={{background:"var(--cream, #FAF8F3)", minHeight:"100vh", padding:"32px 40px", fontFamily:"'Jost', sans-serif", color:"var(--navy, #1B2340)"}} data-testid="admin-inventory-v2">
      <header style={{marginBottom:24}}>
        <div style={{fontSize:11, letterSpacing:"0.18em", color:"var(--gold, #B8913A)"}}>MÉTIER · ADMIN · INVENTORY V2</div>
        <h1 style={{fontFamily:"'Cormorant Garamond', serif", fontSize:42, margin:"6px 0 4px"}}>Inventory V2 preview</h1>
        <p style={{maxWidth:640, color:"#555"}}>Stage 2 · shadow-writes, parity, and deferred classifications. Read-only aside from deferred-classification approvals. Not linked from the public site.</p>
      </header>

      {/* Tabs */}
      <div style={{display:"flex", gap:2, marginBottom:24, borderBottom:"1px solid #ddd"}}>
        {["overview", "records", "deferred", "outbox"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            style={{
              background: tab === t ? "var(--navy, #1B2340)" : "transparent",
              color: tab === t ? "#fff" : "var(--navy, #1B2340)",
              border: "none",
              padding: "10px 20px",
              fontSize: 11, letterSpacing:"0.14em", textTransform:"uppercase",
              cursor:"pointer", fontFamily:"inherit",
            }}>{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && overview && (
        <div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:16}}>
            {[
              ["Organizations", overview.organizations],
              ["Locations", overview.locations],
              ["Resources", overview.resources],
              ["Workspaces", overview.workspaces],
              ["Venues", overview.venues],
              ["Published", overview.published],
              ["Deferred locations", overview.deferred_locations],
            ].map(([label, val]) => (
              <div key={label} style={{background:"#fff", padding:20, border:"1px solid #eee", borderRadius:8}}>
                <div style={{fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"#888"}}>{label}</div>
                <div style={{fontFamily:"'Cormorant Garamond', serif", fontSize:38, marginTop:4}}>{val}</div>
              </div>
            ))}
          </div>
          <h2 style={{marginTop:32, fontFamily:"'Cormorant Garamond', serif"}}>Shadow-write outbox</h2>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:16}}>
            {["pending", "retrying", "synced", "failed", "total"].map(k => (
              <div key={k} style={{background:"#fff", padding:16, border:"1px solid #eee", borderRadius:8}}>
                <div style={{fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:"#888"}}>{k}</div>
                <div style={{fontFamily:"'Cormorant Garamond', serif", fontSize:28}}>{overview.outbox[k]}</div>
              </div>
            ))}
          </div>
          <p style={{marginTop:20, fontSize:12, color:"#666"}}>Feature flag: <code>FEATURE_INVENTORY_V2</code> = <strong>{String(overview.feature_flag)}</strong></p>
        </div>
      )}

      {/* RECORDS */}
      {tab === "records" && (
        <div>
          <h2 style={{fontFamily:"'Cormorant Garamond', serif"}}>V2 resources ({records.length})</h2>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
              <thead>
                <tr style={{borderBottom:"1px solid #ccc", textAlign:"left"}}>
                  <th style={{padding:8}}>Org</th><th>Location</th><th>Resource</th>
                  <th>Category</th><th>Subtype</th><th>City / ST</th>
                  <th>Booking mode</th><th>Mgmt</th>
                  <th>Shadow status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.resource_id} style={{borderBottom:"1px solid #eee"}}>
                    <td style={{padding:8}}>{r.organization_name}</td>
                    <td>{r.location_name}</td>
                    <td>{r.resource_name}</td>
                    <td>{r.inventory_category}</td>
                    <td>{r.workspace_type || r.venue_type || "—"}</td>
                    <td>{r.city}{r.state ? `, ${r.state}` : ""}</td>
                    <td>{r.booking_mode}</td>
                    <td>{r.management_mode}</td>
                    <td><StatusPill status={r.shadow_status} /></td>
                    <td>
                      <button onClick={() => api.get(`/admin/inventory-v2/compare/${(r.location_slug || r.resource_slug)}`).then(x => setDetail(x.data)).catch(e => setError(String(e)))}
                        data-testid={`compare-${r.resource_id}`}
                        style={{padding:"4px 10px", fontSize:11, background:"transparent", border:"1px solid var(--navy)", cursor:"pointer", letterSpacing:"0.08em"}}>COMPARE</button>{" "}
                      <a href={`/locations/${r.location_slug}${r.inventory_category === "venue" ? "/venues/" : "/workspaces/"}${r.resource_slug}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`v2-preview-${r.resource_id}`}
                        style={{padding:"4px 10px", fontSize:11, background:"var(--navy)", color:"#fff", textDecoration:"none", letterSpacing:"0.08em", marginLeft:4}}>PREVIEW</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {detail && (
            <div style={{marginTop:24, background:"#fff", padding:20, border:"1px solid #ddd", borderRadius:8}}>
              <h3 style={{fontFamily:"'Cormorant Garamond', serif"}}>Side-by-side · parity: <StatusPill status={detail.parity.parity} /></h3>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginTop:12}}>
                <div>
                  <h4>Legacy record</h4>
                  <pre style={{background:"#f8f7f2", padding:12, fontSize:11, overflow:"auto", maxHeight:360}}>{JSON.stringify(detail.legacy_view, null, 2)}</pre>
                </div>
                <div>
                  <h4>V2 resolver output</h4>
                  <pre style={{background:"#f8f7f2", padding:12, fontSize:11, overflow:"auto", maxHeight:360}}>{JSON.stringify(detail.resources, null, 2)}</pre>
                </div>
              </div>
              {detail.parity.diffs?.length > 0 && (
                <>
                  <h4>Diffs</h4>
                  <ul style={{fontSize:12}}>
                    {detail.parity.diffs.map((d, i) => (
                      <li key={i}><StatusPill status={d.classification.includes("payment") ? "payment_risk" : "warn"} /> <strong>{d.field}</strong>, legacy: <code>{JSON.stringify(d.legacy)}</code> · v2: <code>{JSON.stringify(d.v2)}</code></li>
                    ))}
                  </ul>
                </>
              )}
              <button onClick={() => setDetail(null)} style={{marginTop:12}}>Close</button>
            </div>
          )}
        </div>
      )}

      {/* DEFERRED */}
      {tab === "deferred" && (
        <div>
          <h2 style={{fontFamily:"'Cormorant Garamond', serif"}}>Deferred classifications ({deferred.length})</h2>
          <div style={{display:"flex", gap:10, alignItems:"center", marginBottom:12}}>
            <a
              href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/inventory-v2/deferred.csv`}
              target="_blank" rel="noreferrer"
              data-testid="deferred-csv-export"
              style={{padding:"6px 14px", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase",
                      border:"1px solid var(--navy)", color:"var(--navy)", textDecoration:"none"}}
            >Export CSV</a>
            <span style={{fontSize:12, color:"#666"}}>
              Includes name, slug, address, description, image, amenities, booking URL, pricing, founder classification, date, notes, readiness. No secrets.
            </span>
          </div>
          <p style={{fontSize:13, color:"#666"}}>
            These legacy workspaces have not been classified yet. No recommendation is made, pick the correct category from the record shown below.
          </p>
          {deferred.map(d => (
            <div key={d.legacy_slug} style={{background:"#fff", padding:20, border:"1px solid #ddd", borderRadius:8, marginBottom:16}} data-testid={`deferred-${d.legacy_slug}`}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"start", gap:16}}>
                <div style={{flex:1}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond', serif", margin:0}}>{d.name}</h3>
                  <div style={{fontSize:12, color:"#666"}}>{d.address || `${d.neighborhood || ""} · ${d.city || ""}`}</div>
                  <p style={{fontSize:13, marginTop:8, maxWidth:640}}>{d.description || <em>(no description)</em>}</p>
                  <div style={{display:"flex", gap:12, marginTop:8, fontSize:12, color:"#555", flexWrap:"wrap"}}>
                    <span>Type: <strong>{d.type || "—"}</strong></span>
                    <span>Booking: <a href={d.booking_url || "#"} target="_blank" rel="noreferrer" style={{color:"var(--gold)"}}>{d.booking_url ? "link" : "—"}</a></span>
                    <span>Existing decision: <StatusPill status={d.classification_decision || null} /></span>
                  </div>
                  {d.amenities?.length > 0 && (
                    <div style={{fontSize:12, marginTop:6, color:"#555"}}>Amenities: {d.amenities.join(" · ")}</div>
                  )}
                </div>
                {d.img && <Img src={d.img} alt="" style={{width:140, height:100, objectFit:"cover", borderRadius:6}} />}
              </div>
              <div style={{marginTop:12, display:"flex", flexWrap:"wrap", gap:8}}>
                {DECISIONS.map(dec => (
                  <button key={dec}
                    onClick={async () => {
                      try {
                        await api.post(`/admin/inventory-v2/deferred/${d.legacy_slug}/classify`, { decision: dec });
                        await load();
                      } catch (e) { setError(String(e)); }
                    }}
                    data-testid={`classify-${d.legacy_slug}-${dec}`}
                    style={{
                      padding:"6px 14px", fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase",
                      border:"1px solid var(--navy)",
                      background: d.classification_decision === dec ? "var(--navy)" : "transparent",
                      color: d.classification_decision === dec ? "#fff" : "var(--navy)",
                      cursor:"pointer",
                    }}>{dec.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* OUTBOX */}
      {tab === "outbox" && (
        <div>
          <h2 style={{fontFamily:"'Cormorant Garamond', serif"}}>Outbox events ({outbox.length})</h2>
          <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
            <thead>
              <tr style={{borderBottom:"1px solid #ccc", textAlign:"left"}}>
                <th style={{padding:8}}>Event</th><th>Kind</th><th>Key</th><th>Status</th><th>Attempts</th><th>Error</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {outbox.map(e => (
                <tr key={e.event_id} style={{borderBottom:"1px solid #eee"}}>
                  <td style={{padding:8, fontFamily:"monospace", fontSize:10}}>{e.event_id.slice(0, 12)}…</td>
                  <td>{e.kind}</td>
                  <td>{JSON.stringify(e.legacy_key)}</td>
                  <td><StatusPill status={e.status} /></td>
                  <td>{e.attempts}</td>
                  <td style={{fontSize:11, color:"#a12b1c", maxWidth:220, wordBreak:"break-word"}}>{e.last_error || "—"}</td>
                  <td>
                    <button onClick={async () => {
                      try { await api.post(`/admin/inventory-v2/outbox/${e.event_id}/retry`); await load(); }
                      catch (err) { setError(String(err)); }
                    }} data-testid={`retry-${e.event_id}`}
                      style={{padding:"3px 10px", fontSize:11}}>Retry</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
